/**
 * The delivery challan as it travels with the goods.
 *
 * The accounting voucher print carries the tax document; what the driver, the
 * gate and the customer's store need is different: vehicle, driver, LR and
 * e-way bill, and for every line the batch with its manufacture and expiry
 * dates, with space to sign for receipt.
 *
 * Everything on the page comes from the dispatch itself. It used to fetch the
 * letterhead and the party address from the accounting print service in a
 * second call; that service was removed with the accounting routes, and the
 * page went on printing challans with no company name, no customer address,
 * no HSN and no e-way bill number, because every one of those was read
 * through a fallback of an empty object. A challan missing them is not a document a lorry can
 * carry.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { api } from '@/modules/accounting/lib/api';
import { reportError } from '@/modules/accounting/lib/store';
import { Button, EmptyState, Spinner } from '@/modules/accounting/components/ui';
import { date, money, qty as fmtQty } from '@/modules/accounting/lib/format';
import { Select } from '@/components/ui/select';

const COPIES = ['Original — for buyer', 'Duplicate — for transporter', 'Triplicate — for supplier'];

export function DispatchPrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dispatch, setDispatch] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [copy, setCopy] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setDispatch(await api.get(`/erp/dispatch/dispatches/${id}`));
      } catch (err) {
        reportError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="page"><Spinner /></div>;
  if (!dispatch) return <div className="page"><EmptyState title="Dispatch not found" /></div>;
  if (!dispatch.dc_voucher_id || dispatch.dc_status === 'CANCELLED') {
    return (
      <div className="page">
        <EmptyState
          title="No challan to print"
          message="A challan exists once the dispatch has been sent. Draft and cancelled dispatches have nothing to print."
          action={<Button onClick={() => navigate(`/erp/dispatch/${id}`)}>Back to the dispatch</Button>}
        />
      </div>
    );
  }

  // All of this used to come from the accounting print service, which no
  // longer exists; it now travels with the dispatch itself.
  const company = {
    name: dispatch.company_legal_name || dispatch.company_name,
    address1: dispatch.company_address1 as string | null,
    address2: dispatch.company_address2 as string | null,
    city: dispatch.company_city as string | null,
    state_name: dispatch.company_state as string | null,
    pincode: dispatch.company_pincode as string | null,
    gstin: dispatch.company_gstin as string | null,
    phone: dispatch.company_phone as string | null,
  };
  const party = {
    name: dispatch.customer_name,
    address1: dispatch.customer_address1,
    address2: dispatch.customer_address2,
    city: dispatch.customer_city,
    state_name: dispatch.customer_state as string | null,
    state_code: dispatch.customer_state_code as string | null,
    pincode: dispatch.customer_pincode,
    mobile: dispatch.customer_mobile,
    gstin: dispatch.customer_gstin,
  };
  const lines: any[] = dispatch.lines ?? [];
  const totalQty = lines.reduce((s, l) => s + Number(l.qty || 0), 0);
  const totalAmount = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const ewb = dispatch.dc_ewb_no;

  return (
    <div className="bg-ink-200 dark:bg-ink-950 min-h-screen">
      <div className="no-print sticky top-0 z-10 bg-white dark:bg-ink-900 border-b border-ink-200 dark:border-ink-800 px-4 py-2.5 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => navigate(-1)} className="btn-ghost btn-icon btn-sm" aria-label="Back"><ArrowLeft size={16} /></button>
        <span className="text-[15px] font-medium">Delivery challan {dispatch.dc_voucher_no}</span>
        <div className="flex-1" />
        <Select compact value={copy} onChange={(e) => setCopy(Number(e.target.value))}>
          {COPIES.map((c, i) => <option key={c} value={i}>{c}</option>)}
        </Select>
        <Button size="sm" variant="primary" icon={Printer} onClick={() => window.print()}>Print</Button>
      </div>

      <div className="py-6 flex justify-center">
        <div className="print-page bg-white text-black shadow-float" style={{ width: '210mm', minHeight: '297mm', padding: '12mm' }}>
          <div className="text-center border-b-2 border-black pb-2 mb-3">
            <p className="text-[13px]st">{COPIES[copy]}</p>
            <h1 className="text-[17px] font-bold mt-0.5">Delivery Challan</h1>
          </div>

          <div className="flex justify-between gap-4 mb-3">
            <div className="flex-1">
              <p className="text-[15px] font-bold">{company.name}</p>
              <p className="text-[13px] leading-snug mt-0.5">
                {[company.address1, company.address2].filter(Boolean).join(', ')}<br />
                {[company.city, company.state_name, company.pincode].filter(Boolean).join(', ')}
              </p>
              {company.gstin && <p className="text-[13px] font-semibold mt-0.5">GSTIN: {company.gstin}</p>}
            </div>
            <table className="text-[13px] shrink-0">
              <tbody>
                <tr><td className="pr-3 text-gray-600">Challan No</td><td className="font-semibold">{dispatch.dc_voucher_no}</td></tr>
                <tr><td className="pr-3 text-gray-600">Date</td><td className="font-semibold">{date(dispatch.dc_date || dispatch.dispatch_date)}</td></tr>
                <tr><td className="pr-3 text-gray-600">Dispatch No</td><td>{dispatch.dispatch_no}</td></tr>
                {dispatch.so_voucher_no && <tr><td className="pr-3 text-gray-600">Against order</td><td>{dispatch.so_voucher_no} · {date(dispatch.so_date)}</td></tr>}
                {dispatch.si_voucher_no && <tr><td className="pr-3 text-gray-600">Invoice</td><td>{dispatch.si_voucher_no}</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="border border-black mb-3 grid grid-cols-2">
            <div className="p-2 border-r border-black">
              <p className="text-[13px] text-gray-600 font-semibold">Consignee</p>
              <p className="text-[14px] font-bold mt-0.5">{party.name || dispatch.customer_name}</p>
              <p className="text-[13px] leading-snug whitespace-pre-line">
                {dispatch.shipping_address
                  || [party.address1, party.address2, party.city, party.state_name, party.pincode].filter(Boolean).join(', ')}
              </p>
              {(party.gstin || dispatch.customer_gstin) && (
                <p className="text-[13px] font-semibold mt-0.5">GSTIN: {party.gstin || dispatch.customer_gstin}</p>
              )}
              {party.mobile && <p className="text-[13px]">Mobile: {party.mobile}</p>}
            </div>
            <div className="p-2 text-[13px]">
              <p className="text-[13px] text-gray-600 font-semibold">Transport</p>
              <table className="mt-0.5">
                <tbody>
                  <tr><td className="pr-2 text-gray-600">From</td><td>{dispatch.warehouse_name || '—'}</td></tr>
                  <tr><td className="pr-2 text-gray-600">Vehicle</td><td className="font-semibold">{dispatch.vehicle_no || '—'}</td></tr>
                  <tr><td className="pr-2 text-gray-600">Transporter</td><td>{dispatch.transporter_name || '—'}</td></tr>
                  <tr><td className="pr-2 text-gray-600">Driver</td><td>{[dispatch.driver_name, dispatch.driver_phone].filter(Boolean).join(' · ') || '—'}</td></tr>
                  <tr><td className="pr-2 text-gray-600">LR No</td><td>{dispatch.lr_no || '—'}</td></tr>
                  <tr><td className="pr-2 text-gray-600">E-way bill</td><td>{ewb || '—'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-black p-1 w-6">#</th>
                <th className="border border-black p-1 text-left">Item</th>
                <th className="border border-black p-1 w-14">HSN</th>
                <th className="border border-black p-1 w-24">Batch</th>
                <th className="border border-black p-1 w-16">Mfg</th>
                <th className="border border-black p-1 w-16">Expiry</th>
                <th className="border border-black p-1 w-16 text-right">Qty</th>
                <th className="border border-black p-1 w-16 text-right">Rate</th>
                <th className="border border-black p-1 w-20 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id}>
                  <td className="border border-black p-1 text-center">{i + 1}</td>
                  <td className="border border-black p-1">
                    <span className="font-semibold">{l.product_name}</span>
                    <span className="text-gray-600"> · {l.product_code}</span>
                  </td>
                  <td className="border border-black p-1 text-center">{l.hsn_code || '—'}</td>
                  <td className="border border-black p-1 text-center">{l.batch_no || '—'}</td>
                  <td className="border border-black p-1 text-center">{l.mfg_date ? date(l.mfg_date, 'DD/MM/YY') : '—'}</td>
                  <td className="border border-black p-1 text-center">{l.expiry_date ? date(l.expiry_date, 'DD/MM/YY') : '—'}</td>
                  <td className="border border-black p-1 text-right tabular">{fmtQty(l.qty)} {l.unit_code || ''}</td>
                  <td className="border border-black p-1 text-right tabular">{money(l.rate)}</td>
                  <td className="border border-black p-1 text-right tabular">{money(l.amount)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="border border-black p-1 text-right" colSpan={6}>Total</td>
                <td className="border border-black p-1 text-right tabular">{fmtQty(totalQty)}</td>
                <td className="border border-black p-1" />
                <td className="border border-black p-1 text-right tabular">{money(totalAmount)}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-[13px] text-gray-600 mt-1">
            Values are before tax. This challan accompanies the goods; the tax invoice is issued separately.
          </p>

          {dispatch.remarks && <p className="text-[13px] mt-2"><span className="font-semibold">Remarks:</span> {dispatch.remarks}</p>}

          <div className="grid grid-cols-3 gap-4 mt-14 text-[13px] text-center">
            <div><div className="border-t border-black pt-1">Prepared by{dispatch.dispatched_by_name ? ` — ${dispatch.dispatched_by_name}` : ''}</div></div>
            <div><div className="border-t border-black pt-1">Driver&apos;s signature</div></div>
            <div><div className="border-t border-black pt-1">Received in good condition</div></div>
          </div>
          <p className="text-[13px] text-right mt-6">For {company.name}</p>
          <p className="text-[13px] text-right mt-8">Authorised signatory</p>
        </div>
      </div>
    </div>
  );
}
